import {
  sectionHasMarks,
  sectionSummaryHasMarks,
} from "./oralSessionStatsHelpers.mjs";

// Category Performance aggregation for the Part 2 video bank.
//
// Only the OSCE categories use "Diagnosis"/"Examination" mark-scheme sections.
// Viva categories use "Question 1..N" (Health promotion/AER), multi-word titles
// ("Diagnosis and Differentials", "Investigations", "Knowledge", "Management"
// for Patient Management) or a mix ("Interpretation", "Diagnosis", ... for
// Investigations). Scoring must therefore never depend on section titles:
// every marked section counts, and the Diagnosis/Examination match only
// controls the split columns. A split with no matching marked sections is
// null (rendered as "–"), never a 0% that reads as failure, and categories
// without any scored section are omitted so they cannot dilute the overall.
export const normalizeVideoSectionType = (title) =>
  String(title || "").toLowerCase().replace(/\s/g, "");

const meanRoundedOrNull = (values) =>
  values.length > 0
    ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
    : null;

const scoreLegacySection = (section) => {
  let points = 0;
  let maxPoints = 0;
  const checkboxes = Array.isArray(section?.checkboxes)
    ? section.checkboxes
    : [];
  checkboxes.forEach((checkbox) => {
    if (checkbox?.type === "rating") {
      maxPoints += 1;
      const ratingValue = Number(checkbox.value || 0);
      points += Number.isFinite(ratingValue) ? ratingValue / 3 : 0;
    } else {
      maxPoints += 1;
      points += checkbox?.value ? 1 : 0;
    }
  });
  return { points, maxPoints };
};

const createBucket = () => ({ diagnosis: [], examination: [], other: [] });

const pushScoredSection = (buckets, category, sectionType, percentage) => {
  if (!Number.isFinite(percentage)) return;
  const bucket = buckets[category] || (buckets[category] = createBucket());
  if (sectionType === "diagnosis") {
    bucket.diagnosis.push(percentage);
  } else if (sectionType === "examination") {
    bucket.examination.push(percentage);
  } else {
    bucket.other.push(percentage);
  }
};

export const computeVideoBankCategoryStats = (sessions) => {
  const buckets = {};
  const sessionList = Array.isArray(sessions) ? sessions : [];

  for (const session of sessionList) {
    const sectionSummaries = Array.isArray(session?.sectionSummaries)
      ? session.sectionSummaries
      : null;

    if (sectionSummaries) {
      for (const section of sectionSummaries) {
        if (!sectionSummaryHasMarks(section)) continue;
        if (typeof section?.percentage !== "number") continue;
        pushScoredSection(
          buckets,
          section.stationCategory || "Uncategorised",
          normalizeVideoSectionType(section.title),
          section.percentage,
        );
      }
      continue;
    }

    for (const station of Array.isArray(session?.stations)
      ? session.stations
      : []) {
      const category = station?.category || "Uncategorised";
      const sections = Array.isArray(station?.markSchemeSections)
        ? station.markSchemeSections
        : [];
      for (const section of sections) {
        if (!sectionHasMarks(section)) continue;
        const { points, maxPoints } = scoreLegacySection(section);
        if (!(maxPoints > 0)) continue;
        pushScoredSection(
          buckets,
          category,
          normalizeVideoSectionType(section?.title),
          Math.round((points / maxPoints) * 100),
        );
      }
    }
  }

  const categories = Object.entries(buckets)
    .map(([category, bucket]) => {
      const scoredCount =
        bucket.diagnosis.length +
        bucket.examination.length +
        bucket.other.length;
      if (scoredCount === 0) return null;
      const diagnosis = meanRoundedOrNull(bucket.diagnosis);
      const examination = meanRoundedOrNull(bucket.examination);
      let aggregate;
      if (
        bucket.diagnosis.length + bucket.examination.length > 0 &&
        bucket.other.length === 0
      ) {
        // Pure Diagnosis/Examination category (every OSCE station): preserve the
        // long-standing (diagnosis + examination) / 2 definition bit-for-bit.
        aggregate = Math.round(((diagnosis || 0) + (examination || 0)) / 2);
      } else {
        // Viva or mixed categories: pool every scored section so marked work
        // always counts, whatever the section titles are.
        aggregate = meanRoundedOrNull([
          ...bucket.diagnosis,
          ...bucket.examination,
          ...bucket.other,
        ]);
      }
      return {
        category,
        diagnosis,
        examination,
        aggregate,
        attempts: Math.max(
          bucket.diagnosis.length,
          bucket.examination.length,
          bucket.other.length,
        ),
      };
    })
    .filter((row) => row !== null)
    .sort((a, b) => b.attempts - a.attempts);

  const meanOfPresent = (values) => {
    const present = values.filter((value) => typeof value === "number");
    return meanRoundedOrNull(present);
  };

  return {
    categories,
    overallDiagnosis: meanOfPresent(categories.map((row) => row.diagnosis)),
    overallExamination: meanOfPresent(
      categories.map((row) => row.examination),
    ),
    overallAggregate:
      categories.length > 0
        ? Math.round(
            categories.reduce((sum, row) => sum + row.aggregate, 0) /
              categories.length,
          )
        : 0,
  };
};
